export interface Settings {
  id: number;
  key: string;
  value: any;
  valueKey?: string;
  type: SettingType;
  category: SettingCategory;
  description?: string;
  descriptionKey?: string;
  isSystemSetting: boolean;
  isEncrypted: boolean;
  isReadOnly: boolean;
  validationRules?: ValidationRule[];
  defaultValue?: any;
  options?: SettingOption[];
  createdDate: Date;
  updatedDate: Date;
  updatedBy?: number;
}

export enum SettingType {
  STRING = 'string',
  NUMBER = 'number',
  BOOLEAN = 'boolean',
  JSON = 'json',
  ARRAY = 'array',
  OBJECT = 'object',
  EMAIL = 'email',
  URL = 'url',
  DATE = 'date',
  TIME = 'time',
  DATETIME = 'datetime',
  COLOR = 'color',
  FILE = 'file',
  PASSWORD = 'password',
}

export enum SettingCategory {
  GENERAL = 'general',
  SECURITY = 'security',
  NOTIFICATIONS = 'notifications',
  EMAIL = 'email',
  SMS = 'sms',
  STORAGE = 'storage',
  INTEGRATION = 'integration',
  BACKUP = 'backup',
  LOGGING = 'logging',
  PERFORMANCE = 'performance',
  UI = 'ui',
  BUSINESS = 'business',
  COMPLIANCE = 'compliance',
  SYSTEM = 'system',
}

export interface ValidationRule {
  id: number;
  settingId: number;
  ruleType: ValidationRuleType;
  value: any;
  message: string;
  isActive: boolean;
}

export enum ValidationRuleType {
  REQUIRED = 'required',
  MIN_LENGTH = 'min_length',
  MAX_LENGTH = 'max_length',
  MIN_VALUE = 'min_value',
  MAX_VALUE = 'max_value',
  PATTERN = 'pattern',
  EMAIL_FORMAT = 'email_format',
  URL_FORMAT = 'url_format',
  UNIQUE = 'unique',
  CUSTOM = 'custom',
}

export interface SettingOption {
  id: number;
  settingId: number;
  label: string;
  value: any;
  description?: string;
  isDefault: boolean;
  isActive: boolean;
  sortOrder: number;
}

export interface SettingGroup {
  id: number;
  name: string;
  displayName: string;
  description?: string;
  category: SettingCategory;
  settings: Settings[];
  isActive: boolean;
  sortOrder: number;
  createdDate: Date;
  updatedDate: Date;
}

export interface SettingHistory {
  id: number;
  settingId: number;
  oldValue: any;
  newValue: any;
  changedBy: number;
  changeReason?: string;
  changedDate: Date;
  ipAddress?: string;
  userAgent?: string;
}

export interface SettingTemplate {
  id: number;
  name: string;
  description?: string;
  category: SettingCategory;
  settings: SettingTemplateItem[];
  isActive: boolean;
  createdDate: Date;
  updatedDate: Date;
  version: string;
}

export interface SettingTemplateItem {
  id: number;
  templateId: number;
  key: string;
  value: any;
  type: SettingType;
  description?: string;
  isRequired: boolean;
}

export interface SystemConfiguration {
  id: number;
  applicationName: string;
  version: string;
  environment: Environment;
  timezone: string;
  locale: string;
  currency: string;
  dateFormat: string;
  timeFormat: '12h' | '24h';
  maxFileSize: number;
  allowedFileTypes: string[];
  sessionTimeout: number;
  passwordPolicy: PasswordPolicy;
  emailSettings: EmailSettings;
  smsSettings: SmsSettings;
  storageSettings: StorageSettings;
  backupSettings: BackupSettings;
  loggingSettings: LoggingSettings;
  securitySettings: SecuritySettings;
  isActive: boolean;
  createdDate: Date;
  updatedDate: Date;
}

export enum Environment {
  DEVELOPMENT = 'development',
  STAGING = 'staging',
  PRODUCTION = 'production',
  TEST = 'test',
}

export interface PasswordPolicy {
  minLength: number;
  maxLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumbers: boolean;
  requireSpecialChars: boolean;
  maxAge: number;
  minAge: number;
  historyCount: number;
  lockoutAttempts: number;
  lockoutDuration: number;
}

export interface EmailSettings {
  smtpHost: string;
  smtpPort: number;
  smtpUsername: string;
  smtpPassword: string;
  smtpEncryption: 'none' | 'ssl' | 'tls';
  fromEmail: string;
  fromName: string;
  replyToEmail?: string;
  replyToName?: string;
  isActive: boolean;
}

export interface SmsSettings {
  provider: SmsProvider;
  apiKey: string;
  apiSecret?: string;
  fromNumber: string;
  isActive: boolean;
}

export enum SmsProvider {
  TWILIO = 'twilio',
  AWS_SNS = 'aws_sns',
  FIREBASE = 'firebase',
  CUSTOM = 'custom',
}

export interface StorageSettings {
  type: StorageType;
  localPath?: string;
  s3Bucket?: string;
  s3Region?: string;
  s3AccessKey?: string;
  s3SecretKey?: string;
  azureAccount?: string;
  azureKey?: string;
  azureContainer?: string;
  maxFileSize: number;
  allowedFileTypes: string[];
  isActive: boolean;
}

export enum StorageType {
  LOCAL = 'local',
  AWS_S3 = 'aws_s3',
  AZURE_BLOB = 'azure_blob',
  GOOGLE_CLOUD = 'google_cloud',
  CUSTOM = 'custom',
}

export interface BackupSettings {
  isEnabled: boolean;
  frequency: BackupFrequency;
  retentionDays: number;
  storageType: StorageType;
  storagePath: string;
  includeFiles: boolean;
  includeDatabase: boolean;
  compressionEnabled: boolean;
  encryptionEnabled: boolean;
  lastBackupDate?: Date;
  nextBackupDate?: Date;
}

export enum BackupFrequency {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  MANUAL = 'manual',
}

export interface LoggingSettings {
  level: LogLevel;
  maxFileSize: number;
  maxFiles: number;
  retentionDays: number;
  includeAuditLogs: boolean;
  includeErrorLogs: boolean;
  includeAccessLogs: boolean;
  includePerformanceLogs: boolean;
  isActive: boolean;
}

export enum LogLevel {
  ERROR = 'error',
  WARN = 'warn',
  INFO = 'info',
  TRACE = 'trace',
}

export interface SecuritySettings {
  twoFactorRequired: boolean;
  sessionTimeout: number;
  maxLoginAttempts: number;
  lockoutDuration: number;
  ipWhitelist: string[];
  ipBlacklist: string[];
  allowedDomains: string[];
  sslRequired: boolean;
  corsEnabled: boolean;
  corsOrigins: string[];
  rateLimitingEnabled: boolean;
  rateLimitRequests: number;
  rateLimitWindow: number;
  auditLogRetentionDays: number;
  isActive: boolean;
}
